// Which view a card of Neo's chart views shows (TOM-711, cloud ADR 0403 §5):
// the owner's own switch, or a "Show RPM" follow-up chip (the answer hook's
// `showView`). Kept per page, per answer and card, and for a stored answer in
// the tab's sessionStorage too, so a reload keeps the view the owner chose.

type Listener = () => void;

const chosen = new Map<string, string>();
const listeners = new Set<Listener>();

/** The key of one card in one answer. */
export function neoChartViewKey(answerId: string | undefined, groupId: string): string {
  return `${answerId ?? 'local'}|${groupId}`;
}

/** Show the view `recordId` of card `groupId` in answer `answerId`. */
export function selectNeoChartView(
  answerId: string | undefined,
  groupId: string,
  recordId: string
): void {
  const key = neoChartViewKey(answerId, groupId);
  if (chosen.get(key) === recordId) return;
  chosen.set(key, recordId);
  for (const l of listeners) l();
}

/** The view a card shows, or undefined for its first. */
export function neoChartView(key: string): string | undefined {
  return chosen.get(key);
}

/** For useSyncExternalStore. */
export function subscribeNeoChartViews(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const STORE_PREFIX = 'neo-chart-view:';

/** The view a stored answer's card showed in this tab, or undefined. */
export function recalledNeoChartView(
  storedId: string | undefined,
  groupId: string
): string | undefined {
  if (storedId === undefined) return undefined;
  try {
    return globalThis.sessionStorage?.getItem(`${STORE_PREFIX}${storedId}|${groupId}`) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Keep a stored answer's card on its view for this tab. */
export function rememberNeoChartView(
  storedId: string | undefined,
  groupId: string,
  recordId: string
): void {
  if (storedId === undefined) return;
  try {
    globalThis.sessionStorage?.setItem(`${STORE_PREFIX}${storedId}|${groupId}`, recordId);
  } catch {
    // Storage blocked: the choice lasts until the page is reloaded.
  }
}

/** Forget every choice (tests). */
export function resetNeoChartViews(): void {
  chosen.clear();
  for (const l of listeners) l();
}
